import { alpha, Box, Typography, Chip, Accordion, AccordionSummary, AccordionDetails, Button, Stack, CircularProgress, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import { Download, Email, ArrowDropDown, Description, Summarize } from "@mui/icons-material";
import { TrackMasterResponse } from "../../../services/agent.service";
import { ProductMetadata } from "./types";
import { Section, CreatedBy, CrmProductInfo, AttachedFiles, InfoRow } from "./common";
import { format } from "date-fns";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import { downloadTrackMasterPDF, getTrackMasterPDFBlob, downloadTrackMasterMultiPDF, getTrackMasterMultiPDFBlob } from "../../../utils/pdfGenerator";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../store/system.store";

const TrackMasterDetails: React.FC<{
  productInfo: TrackMasterResponse;
  productMetaData: ProductMetadata;
  clientName?: string;
  clientEmail?: string;
  ownerEmail?: string;
}> = ({ productInfo, productMetaData, clientName, clientEmail, ownerEmail }) => {
  const { createdBy, product } = productInfo;
  const dispatch = useDispatch();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [emailMenuAnchor, setEmailMenuAnchor] = useState<null | HTMLElement>(null);

  // Fixed: product is now a single object, not an array
  const currentProduct = product;

  if (!currentProduct) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography>Product not found</Typography>
      </Box>
    );
  }

  const trackingData = currentProduct.data && currentProduct.data.length > 0 ? currentProduct.data : [];

  const handleDownloadMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  const handleEmailMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setEmailMenuAnchor(event.currentTarget);
  };

  const handleEmailMenuClose = () => {
    setEmailMenuAnchor(null);
  };

  const handleDownloadSinglePDF = async (index: number) => {
    handleDownloadMenuClose();

    if (trackingData.length === 0) {
      dispatch(
        showSnackbar({
          isError: true,
          msg: "No tracking data available to generate PDF",
        })
      );
      return;
    }

    setIsDownloading(true);
    try {
      const dataItem = trackingData[index];
      downloadTrackMasterPDF({
        dataItem,
        sessionId: `${currentProduct.id}_entry_${index + 1}`,
        clientFullName: clientName,
        clientEmail: clientEmail,
      });
      dispatch(
        showSnackbar({
          isError: false,
          msg: "PDF downloaded successfully",
        })
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
      dispatch(
        showSnackbar({
          isError: true,
          msg: "Failed to generate PDF. Please try again.",
        })
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadAllPDF = async () => {
    handleDownloadMenuClose();

    if (trackingData.length === 0) {
      dispatch(
        showSnackbar({
          isError: true,
          msg: "No tracking data available to generate PDF",
        })
      );
      return;
    }

    setIsDownloading(true);
    try {
      downloadTrackMasterMultiPDF({
        dataItems: trackingData,
        sessionId: currentProduct.id,
        clientFullName: clientName,
        clientEmail: clientEmail,
      });
      dispatch(
        showSnackbar({
          isError: false,
          msg: `Comprehensive PDF with ${trackingData.length} entries downloaded successfully`,
        })
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
      dispatch(
        showSnackbar({
          isError: true,
          msg: "Failed to generate PDF. Please try again.",
        })
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSendSingleEmail = async (index: number) => {
    handleEmailMenuClose();

    if (trackingData.length === 0) {
      dispatch(
        showSnackbar({
          isError: true,
          msg: "No tracking data available to generate PDF",
        })
      );
      return;
    }

    setIsSendingEmail(true);
    try {
      const dataItem = trackingData[index];
      const pdfBlob = await getTrackMasterPDFBlob({
        dataItem,
        sessionId: `${currentProduct.id}_entry_${index + 1}`,
        clientFullName: clientName,
        clientEmail: clientEmail,
      });

      const pdfUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `${currentProduct.id}_entry_${index + 1}.pdf`;

      const recipientName = clientName || 'Client';
      const recipientEmail = clientEmail || '';
      const subject = encodeURIComponent(`Track Master Report`);
      const body = encodeURIComponent(
        `Dear ${recipientName},\n\nPlease find attached the Track Master investigation report.\n\nBest regards,\nOnline Justice Team`
      );

      // Build mailto link with CC for owner
      let mailtoLink = recipientEmail ? `mailto:${recipientEmail}` : 'mailto:';
      const params = [];
      if (ownerEmail && ownerEmail.trim()) {
        params.push(`cc=${encodeURIComponent(ownerEmail)}`);
      }
      params.push(`subject=${subject}`);
      params.push(`body=${body}`);
      mailtoLink += `?${params.join('&')}`;

      link.click();
      window.location.href = mailtoLink;
      URL.revokeObjectURL(pdfUrl);

      dispatch(
        showSnackbar({
          isError: false,
          msg: "PDF downloaded. Please attach it to your email client.",
        })
      );
    } catch (error) {
      console.error("Error preparing email:", error);
      dispatch(
        showSnackbar({
          isError: true,
          msg: "Failed to prepare email. Please try again.",
        })
      );
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleSendAllEmail = async () => {
    handleEmailMenuClose();

    if (trackingData.length === 0) {
      dispatch(
        showSnackbar({
          isError: true,
          msg: "No tracking data available to generate PDF",
        })
      );
      return;
    }

    setIsSendingEmail(true);
    try {
      const pdfBlob = await getTrackMasterMultiPDFBlob({
        dataItems: trackingData,
        sessionId: currentProduct.id,
        clientFullName: clientName,
        clientEmail: clientEmail,
      });

      const pdfUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `${currentProduct.id}_comprehensive.pdf`;

      const recipientName = clientName || 'Client';
      const recipientEmail = clientEmail || '';
      const subject = encodeURIComponent(`Track Master Report`);
      const body = encodeURIComponent(
        `Dear ${recipientName},\n\nPlease find attached the Track Master investigation report.\n\nBest regards,\nOnline Justice Team`
      );

      // Build mailto link with CC for owner
      let mailtoLink = recipientEmail ? `mailto:${recipientEmail}` : 'mailto:';
      const params = [];
      if (ownerEmail && ownerEmail.trim()) {
        params.push(`cc=${encodeURIComponent(ownerEmail)}`);
      }
      params.push(`subject=${subject}`);
      params.push(`body=${body}`);
      mailtoLink += `?${params.join('&')}`;

      link.click();
      window.location.href = mailtoLink;
      URL.revokeObjectURL(pdfUrl);

      dispatch(
        showSnackbar({
          isError: false,
          msg: `Comprehensive PDF with ${trackingData.length} entries downloaded. Please attach it to your email client.`,
        })
      );
    } catch (error) {
      console.error("Error preparing email:", error);
      dispatch(
        showSnackbar({
          isError: true,
          msg: "Failed to prepare email. Please try again.",
        })
      );
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Action Buttons */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <Download />}
          endIcon={<ArrowDropDown />}
          onClick={handleDownloadMenuClick}
          disabled={isDownloading || trackingData.length === 0}
          fullWidth
        >
          {isDownloading ? "Generating PDF..." : "Download PDF"}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          startIcon={isSendingEmail ? <CircularProgress size={20} color="inherit" /> : <Email />}
          endIcon={<ArrowDropDown />}
          onClick={handleEmailMenuClick}
          disabled={isSendingEmail || trackingData.length === 0}
          fullWidth
        >
          {isSendingEmail ? "Preparing Email..." : "Send Email with PDF"}
        </Button>
      </Stack>

      {/* Download Menu */}
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={handleDownloadMenuClose}
      >
        <MenuItem onClick={handleDownloadAllPDF}>
          <ListItemIcon>
            <Summarize fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Download All Entries"
            secondary={`Comprehensive PDF with ${trackingData.length} entries`}
          />
        </MenuItem>
        {trackingData.length > 0 && trackingData.map((item, index) => (
          <MenuItem key={index} onClick={() => handleDownloadSinglePDF(index)}>
            <ListItemIcon>
              <Description fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={`Entry ${index + 1} - ${item.ip}`}
              secondary={`${item.country} - ${item.ipDetails?.city_name || 'Unknown'}`}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* Email Menu */}
      <Menu
        anchorEl={emailMenuAnchor}
        open={Boolean(emailMenuAnchor)}
        onClose={handleEmailMenuClose}
      >
        <MenuItem onClick={handleSendAllEmail}>
          <ListItemIcon>
            <Summarize fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Email All Entries"
            secondary={`Comprehensive PDF with ${trackingData.length} entries`}
          />
        </MenuItem>
        {trackingData.length > 0 && trackingData.map((item, index) => (
          <MenuItem key={index} onClick={() => handleSendSingleEmail(index)}>
            <ListItemIcon>
              <Description fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={`Entry ${index + 1} - ${item.ip}`}
              secondary={`${item.country} - ${item.ipDetails?.city_name || 'Unknown'}`}
            />
          </MenuItem>
        ))}
      </Menu>

      <CrmProductInfo productCrm={productInfo.productCrm} />

      {/* Basic Info */}
      <Section title="Details">
        <InfoRow label="Created at" value={format(new Date(currentProduct.Created_Time), "yyyy-MM-dd HH:mm")} />
        <InfoRow label="Product Request ID" value={currentProduct.id} />
        <InfoRow label="Status" value={currentProduct.status} />
        <InfoRow label="Product Type" value={currentProduct.Name} />
        <InfoRow label="Product Request Type" value={currentProduct.productRequestType} />
        <InfoRow label="Contract Signed" value={currentProduct.contractSigned ? "Yes" : "No"} />
        <InfoRow label="Payment Status" value={productInfo.productCrm?.payed ? "Paid" : "Not Paid"} />
        <InfoRow label="Total Amount" value={currentProduct.totalAmount ? `${currentProduct.totalAmount}` : "Not Set"} />
        <InfoRow label="Paid Amount" value={`${currentProduct.paid}`} />
        <InfoRow label="Left to Pay" value={currentProduct.leftToPay !== null ? `${currentProduct.leftToPay}` : "N/A"} />
        {currentProduct.resultFile && <InfoRow label="Result File" value="Available" />}
      </Section>

      {/* Tracking Link Section */}
      {currentProduct.link && (
        <Section title="Tracking Link">
          <InfoRow label="Tracking Link" value={currentProduct.link} />
        </Section>
      )}
      {currentProduct.deals && currentProduct.deals.length > 0 && (
        <Section title={`Payment Deals (${currentProduct.deals.length})`}>
          {currentProduct.deals.map((deal, index) => (
            <Box
              key={deal.id}
              sx={{
                mb: 3,
                p: 2.5,
                borderLeft: `3px solid ${alpha("#1976d2", 0.4)}`,
                bgcolor: alpha("#1976d2", 0.05),
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}>
                Deal {index + 1}
              </Typography>
              <InfoRow label="Deal ID" value={deal.id} />
              <InfoRow label="Amount" value={`${deal.amount || 0}`} />
              <InfoRow label="Deal Created" value={deal.createdTime ? format(new Date(deal.createdTime), "yyyy-MM-dd HH:mm") : "N/A"} />
              {deal.chargedAmount && <InfoRow label="Charged Amount" value={`${deal.chargedAmount}`} />}
              <InfoRow label="Invoice" value={deal.invoice ? "Available" : "Not Available"} />
            </Box>
          ))}
        </Section>
      )}

      {/* Request Details */}
      {currentProduct.requestDetails && Object.keys(currentProduct.requestDetails).length > 0 && (
        <Section title="Request Details">
          {Object.entries(currentProduct.requestDetails).map(([key, value]) => (
            <InfoRow key={key} label={key} value={String(value)} />
          ))}
        </Section>
      )}

      {/* Tracking Summary */}
      {trackingData.length > 0 && (
        <Section title="Tracking Summary">
          <InfoRow label="Total Clicks" value={trackingData.length.toString()} />
          <InfoRow label="Unique IPs" value={[...new Set(trackingData.map((d) => d.ip))].length.toString()} />
          <InfoRow label="Countries" value={[...new Set(trackingData.map((d) => d.country))].join(", ")} />
          <InfoRow label="New Visitors" value={trackingData.filter((d) => d.new).length.toString()} />
          <InfoRow label="Total ISPs" value={[...new Set(trackingData.map((d) => d.isp))].length.toString()} />
        </Section>
      )}

      {/* Click Details - Grouped by IP */}
      {trackingData.length > 0 && (
        <Section title={`Visitors (${[...new Set(trackingData.map((d) => d.ip))].length} unique IPs)`}>
          {(() => {
            // Group clicks by IP
            const groupedByIP = trackingData.reduce((acc: Record<string, typeof trackingData>, click) => {
              if (!acc[click.ip]) {
                acc[click.ip] = [];
              }
              acc[click.ip].push(click);
              return acc;
            }, {});

            // Sort IPs by most recent click
            const sortedIPs = Object.keys(groupedByIP).sort((a, b) => {
              const latestA = Math.max(...groupedByIP[a].map((click) => click.time));
              const latestB = Math.max(...groupedByIP[b].map((click) => click.time));
              return latestB - latestA;
            });

            return sortedIPs.map((ip) => {
              const clicks = groupedByIP[ip].sort((a, b) => b.time - a.time);
              const latestClick = clicks[0];
              const isNewVisitor = clicks.some((click) => click.new);

              return (
                <Accordion
                  key={ip}
                  sx={{
                    mb: 1,
                    bgcolor: isNewVisitor ? alpha("#4caf50", 0.05) : alpha("#ff9800", 0.05),
                    borderLeft: `4px solid ${isNewVisitor ? "#4caf50" : "#ff9800"}`,
                    "&:before": {
                      display: "none",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<GridExpandMoreIcon />}
                    sx={{
                      "& .MuiAccordionSummary-content": {
                        alignItems: "center",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {ip}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {latestClick.country}${latestClick.ipDetails?.city_name ? ` - ${latestClick.ipDetails.city_name}` : ""}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Chip label={`${clicks.length} clicks`} size="small" variant="outlined" color="primary" />
                        <Chip
                          label={isNewVisitor ? "New Visitor" : "Return Visitor"}
                          size="small"
                          color={isNewVisitor ? "success" : "warning"}
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {/* IP Summary */}
                      <Box sx={{ mb: 2, p: 2, bgcolor: alpha("#1976d2", 0.05), borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                          Visitor Summary
                        </Typography>
                        <InfoRow label="IP Address" value={ip} />
                        <InfoRow label="Total Clicks" value={clicks.length.toString()} />
                        <InfoRow label="First Visit" value={format(new Date(Math.min(...clicks.map((c) => c.time)) * 1000), "yyyy-MM-dd HH:mm:ss")} />
                        <InfoRow label="Last Visit" value={format(new Date(Math.max(...clicks.map((c) => c.time)) * 1000), "yyyy-MM-dd HH:mm:ss")} />
                        <InfoRow
                          label="Location"
                          value={`${latestClick.country}${latestClick.ipDetails?.city_name ? ` - ${latestClick.ipDetails.city_name}` : ""}`}
                        />
                        <InfoRow label="ISP" value={latestClick.isp} />
                      </Box>

                      {/* Individual Clicks */}
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Click History ({clicks.length} clicks)
                      </Typography>

                      {clicks.map((click, clickIndex) => (
                        <Accordion
                          key={click.id}
                          sx={{
                            bgcolor: "transparent",
                            boxShadow: "none",
                            border: "1px solid #e0e0e0",
                            "&:before": {
                              display: "none",
                            },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<GridExpandMoreIcon />}
                            sx={{
                              minHeight: "auto",
                              "& .MuiAccordionSummary-content": {
                                margin: "8px 0",
                              },
                            }}
                          >
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Click #{clicks.length - clickIndex} - {format(new Date(click.time * 1000), "yyyy-MM-dd HH:mm:ss")}
                              </Typography>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                {click.new && <Chip label="First Visit" size="small" color="success" variant="outlined" />}
                                {click.archive === 1 && <Chip label="Archived" size="small" color="secondary" variant="outlined" />}
                              </Box>
                            </Box>
                          </AccordionSummary>

                          <AccordionDetails>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              {click.ipDetails && (
                                <>
                                  <InfoRow
                                    label="Full Location"
                                    value={`${click.ipDetails.city_name}, ${click.ipDetails.region_name}, ${click.ipDetails.country_name}`}
                                  />
                                  <InfoRow label="Time Zone" value={click.ipDetails.time_zone} />
                                  <InfoRow label="Connection" value={`${click.ipDetails.net_speed} - ${click.ipDetails.usage_type}`} />
                                  <InfoRow label="Mobile Brand" value={click.ipDetails.mobile_brand || "N/A"} />
                                </>
                              )}

                              <InfoRow label="Domain Path" value={click.domain} />
                              <InfoRow label="User Agent" value={click.host} />

                              {click.source && <InfoRow label="Source" value={click.source} />}
                              {click.brand && <InfoRow label="Brand" value={click.brand} />}
                              {click.photo_url && <InfoRow label="Photo URL" value={click.photo_url} />}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            });
          })()}
        </Section>
      )}

      {/* Created By */}
      <CreatedBy createdBy={createdBy} />

      {/* Attached Files */}
      <AttachedFiles attachments={productInfo.attachments} productMetaData={productMetaData} />
    </Box>
  );
};

export default TrackMasterDetails;
